import Metadata from "./question/metadata";
import Alternative from "./question/alternative";
import SupportText from "./question/support_text";
import Resolution from "./question/resolution";
import { useEffect, useCallback, useRef, useState } from "react";

import clsx from "clsx";

import { question as questionPlaceholder } from "./placeholders";

import { Question as ApiQuestionProps } from "types/question";
import { QuestionnaireAnswer } from "types/questionnaire_answer";
import { QuestionnaireComponentProps } from "components/questionnaire";

interface QuestionProps extends ApiQuestionProps {
  questionnaireAnswer: QuestionnaireAnswer;
  onAnswerChange: (answer: string) => void;
  completedQuestionnaire?: boolean;
  questionnaire?: QuestionnaireComponentProps;
  onFetchLessonsCount?: (count: number) => void;
  loading?: boolean;
  disabled?: boolean;
}

export default function Question(props: QuestionProps): JSX.Element {
  const { disabled } = props;
  const question = props.loading ? questionPlaceholder : props;
  const { content, alternatives, correctAlternative } = question;
  const { questionnaireAnswer, onAnswerChange, completedQuestionnaire } = props;
  const { supportText } = question;

  const mathJaxLoaded = useRef(false);

  const requiresMathJax = useCallback(() => {
    const latexRegex = /\$\$?.*?\$\$?|\\\(.*?\\\)|\\\[.*?\\\]/;
    return (
      latexRegex.test(content) ||
      alternatives.some((alt) => latexRegex.test(alt)) ||
      (supportText?.content && latexRegex.test(supportText.content))
    );
  }, [content, alternatives, supportText]);

  const [loading, setLoading] = useState(
    props.loading || (requiresMathJax() && !mathJaxLoaded.current)
  );

  // Function to load MathJax script
  const loadMathJax = useCallback(() => {
    if (
      typeof window !== "undefined" &&
      !window.MathJax &&
      !mathJaxLoaded.current
    ) {
      mathJaxLoaded.current = true;

      // Configure MathJax
      window.MathJax = {
        svg: {
          fontCache: "global"
        }
      };

      // Create and append script
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/mathjax@3.0.0/es5/tex-chtml.js";
      script.async = true;
      script.onload = () => {
        typesetMath();
      };
      document.head.appendChild(script);
    }
  }, []);

  // Function to handle MathJax typesetting
  const typesetMath = useCallback(() => {
    if (typeof window !== "undefined" && window.MathJax) {
      if (window.MathJax.typeset) {
        window.MathJax.typeset();
      } else if (window.MathJax.Hub && window.MathJax.Hub.Queue) {
        window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub]);
      }
      setTimeout(() => setLoading(false), 100);
    }
  }, []);

  // Load MathJax if required and typeset when content changes
  useEffect(() => {
    if (!props.loading && requiresMathJax()) {
      loadMathJax();

      if (!window.MathJax) return;

      typesetMath();
    } else if (!props.loading && !requiresMathJax()) {
      setLoading(false);
    }
  }, [
    loading,
    requiresMathJax,
    loadMathJax,
    content,
    alternatives,
    supportText,
    typesetMath,
    props.loading
  ]);

  const revealAnswer =
    !loading &&
    (!questionnaireAnswer ||
      completedQuestionnaire ||
      questionnaireAnswer?.seenAnswer);

  const alternativeElems = alternatives.map((alternative, index) => {
    return (
      <div key={index} className="mb-3">
        <Alternative
          index={index}
          content={alternative}
          answer={correctAlternative}
          questionnaireAnswer={questionnaireAnswer}
          revealAnswer={revealAnswer}
          onAnswerChange={onAnswerChange}
          disabled={disabled}
          loading={loading}
        />
      </div>
    );
  });

  return (
    <>
      <div id="question" className="pt-2">
        {!loading && supportText && (
          <div className="mb-8">
            <SupportText {...supportText} />
          </div>
        )}
        <div className="mb-3">
          <Metadata {...question} loading={loading} />
        </div>
        <div
          className={clsx("content fade-loading", { loading, "h-56": loading })}
          dangerouslySetInnerHTML={{ __html: content }}
        ></div>

        <div className="alternatives mt-5">{alternativeElems}</div>
      </div>

      {revealAnswer && (
        <Resolution
          questionnaireAnswer={questionnaireAnswer}
          question={question}
        />
      )}

      {revealAnswer && questionnaireAnswer && !questionnaireAnswer.answer && (
        <span className="flex justify-center my-2 w-full text-error-dark font-semibold uppercase">
          Questão em branco
        </span>
      )}

      <style jsx global>{`
        #question blockquote {
          @apply text-emphasis-low text-sm;
        }
      `}</style>
    </>
  );
}
